import type { McpTool } from '@deskworks/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, RefreshCw, Wrench } from 'lucide-react';
import { useState } from 'react';
import { JsonBlock } from '@/components/code-block';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { isEmptyObject } from '@/lib/format';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/**
 * Makes MCP connection errors readable: the server wraps the remote JSON-RPC error
 * (e.g. `… Error POSTing to endpoint: {"jsonrpc":"2.0","error":{…,"message":"…"}}`) — show the inner message.
 */
export function mcpErrorText(raw: string): string {
  const inner = /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(raw);
  if (!inner) return raw;
  let msg = inner[1];
  try {
    msg = JSON.parse(`"${inner[1]}"`) as string;
  } catch {
    /* keep raw capture */
  }
  // Accept both the German and the English server prefix.
  const prefix = /^(MCP-Server „[^“]*“|MCP server "[^"]*"|MCP server “[^”]*”)/.exec(raw)?.[1];
  return prefix ? `${prefix}: ${msg}` : msg;
}

function mcpErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status !== 0 && err.status !== 401) return mcpErrorText(err.message);
  return err instanceof Error ? err.message : String(err);
}

/** Tools offered by an `mcp_server` connection. */
export function McpToolsSection({
  connectionId,
  connectionFailing,
  className,
}: {
  connectionId: string;
  /** Last health check failed — the cached list may be outdated. */
  connectionFailing?: boolean;
  className?: string;
}) {
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const tools = useQuery({
    queryKey: qk.mcpTools(connectionId),
    queryFn: () => api.connections.mcpTools(connectionId),
    retry: false,
  });
  // No toast here: a failed refresh is shown inline (with a readable message) above the cached list.
  const refresh = useMutation({
    mutationFn: () => api.connections.mcpTools(connectionId, true),
    onMutate: () => setRefreshError(null),
    onError: (err) => setRefreshError(mcpErrorMessage(err)),
    onSuccess: (res) => {
      queryClient.setQueryData(qk.mcpTools(connectionId), res);
      void queryClient.invalidateQueries({ queryKey: qk.connection(connectionId), exact: true });
    },
  });

  const list = tools.data?.tools ?? [];
  const readCount = list.filter((tool) => tool.readOnly).length;

  return (
    <Section
      className={className}
      icon={<Wrench />}
      title={t('Tools')}
      description={t('What agents can do through this MCP server. Read-only tools need the “Read” permission on the desk, all others need “Write”.')}
      actions={
        <Button variant="outline" size="sm" onClick={() => refresh.mutate()} loading={refresh.isPending}>
          {!refresh.isPending && <RefreshCw />} {t('Reload tools')}
        </Button>
      }
    >
      {tools.isPending ? (
        <LoadingRows rows={3} />
      ) : tools.isError ? (
        <ErrorState
          title={t('The MCP server didn’t respond')}
          error={new Error(refreshError ?? mcpErrorMessage(tools.error))}
          onRetry={() => refresh.mutate()}
        />
      ) : list.length === 0 ? (
        <EmptyState
          compact
          title={t('No tools')}
          description={
            tools.data.refreshedAt
              ? t('The server doesn’t offer any tools.')
              : t('The tools haven’t been fetched yet. Click “Reload tools”.')
          }
        />
      ) : (
        <>
          {(refreshError || connectionFailing) && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <div className="min-w-0 break-words">
                {refreshError ? (
                  <>
                    {t('Reload failed: {error}', { error: refreshError })}
                    <br />
                  </>
                ) : (
                  `${t('The last connection check failed.')} `
                )}
                {t('Showing the last list that loaded successfully — it may be out of date.')}
              </div>
            </div>
          )}
          <p className="mb-2 text-xs text-muted-foreground">
            {list.length === 1 ? t('1 Tool') : t('{n} Tools', { n: list.length })} · {t('{n} read-only', { n: readCount })} · {t('{n} writing', { n: list.length - readCount })}
            {tools.data.refreshedAt && (
              <>
                {' '}
                · {tr('fetched {time}', { time: <RelativeTime value={tools.data.refreshedAt} /> })}
              </>
            )}
          </p>
          <ul className="-mx-5 -mb-5 divide-y border-t">
            {list.map((tool) => (
              <ToolRow key={tool.name} tool={tool} />
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}

function ToolRow({ tool }: { tool: McpTool }) {
  const [open, setOpen] = useState(false);
  const hasSchema = !isEmptyObject(tool.inputSchema);
  return (
    <li className="px-5 py-2.5">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={!hasSchema}
          className="mt-0.5 shrink-0 cursor-pointer text-muted-foreground disabled:cursor-default disabled:opacity-30"
          aria-label={t('Show parameters')}
        >
          <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="font-mono text-[13px] font-medium">{tool.name}</code>
            {tool.title && tool.title !== tool.name && <span className="text-sm text-muted-foreground">{tool.title}</span>}
          </div>
          {tool.description && <p className="mt-0.5 line-clamp-3 text-[13px] text-muted-foreground">{tool.description}</p>}
          {open && hasSchema && <JsonBlock value={tool.inputSchema} className="mt-2" maxHeight="max-h-64" />}
        </div>
        {tool.readOnly ? <Badge tone="outline">{t('read-only')}</Badge> : <Badge tone="amber">{t('writes')}</Badge>}
      </div>
    </li>
  );
}
